export const JOURNEY_IDENTITY_EVENT = 'arvento:journey-identity';

const controllers = new WeakMap();

function apiBaseUrl(endpoint) {
  try {
    const url = new URL(endpoint);
    return url.protocol === 'https:' && !url.username && !url.password ? url.origin : '';
  } catch {
    return '';
  }
}

function validSession(value) {
  return value && typeof value.accessToken === 'string' && value.accessToken
    && Number.isSafeInteger(value.expiresAt);
}

function setHostState(root, state, message) {
  root.dataset.journeyHostState = state;
  const status = root.querySelector('[data-arvento-journey-host-status]');
  if (status) status.textContent = message;
  const auth = root.querySelector('[data-customer-account-auth]');
  const pwa = root.querySelector('[data-arvento-journey-pwa-root]');
  if (auth) auth.hidden = state === 'authenticated';
  if (pwa) pwa.hidden = state !== 'authenticated';
}

export function initJourneyHost(root, options = {}) {
  const existing = controllers.get(root);
  if (existing) return existing;

  const windowLike = options.windowLike || window;
  const origin = apiBaseUrl(root.dataset.endpoint);
  if (!origin) {
    setHostState(root, 'configuration_error', 'Journey host configuration is incomplete.');
    const unavailable = { dispose() {} };
    controllers.set(root, unavailable);
    return unavailable;
  }

  if (windowLike.ARVENTO_JOURNEY_HOST) {
    setHostState(root, 'duplicate_host', 'Journey is already mounted on this page.');
    const duplicate = { dispose() {} };
    controllers.set(root, duplicate);
    return duplicate;
  }

  const readSession = options.getJourneySession;
  const clearSession = options.clearJourneySession;
  const mountPwa = options.mountPwa || windowLike.ARVENTO_JOURNEY_PWA?.mount;
  if (typeof readSession !== 'function' || typeof clearSession !== 'function') {
    setHostState(root, 'configuration_error', 'Journey host configuration is incomplete.');
    const unavailable = { dispose() {} };
    controllers.set(root, unavailable);
    return unavailable;
  }
  let unmount = null;
  let disposed = false;

  const bridge = Object.freeze({
    apiBaseUrl: origin,
    mapConfiguration: Object.freeze({ browserApiKey: root.dataset.journeyMapsBrowserKey || '' }),
    getJourneySession: async () => readSession() || null,
    onSessionExpired() {
      clearSession();
      if (unmount) {
        unmount();
        unmount = null;
      }
      setHostState(root, 'session_expired', 'Your Journey session expired. Sign in again to continue.');
    },
  });
  windowLike.ARVENTO_JOURNEY_HOST = bridge;
  setHostState(root, 'unauthenticated', 'Sign in with Shopify to begin your Journey.');

  const mountAuthenticatedPwa = () => {
    if (disposed || unmount) return;
    const session = readSession();
    if (!validSession(session)) {
      setHostState(root, 'authentication_error', 'We could not confirm your Journey session. Please sign in again.');
      return;
    }
    if (typeof mountPwa !== 'function') {
      setHostState(root, 'configuration_error', 'Journey host configuration is incomplete.');
      return;
    }
    const pwa = root.querySelector('[data-arvento-journey-pwa-root]');
    if (!pwa) {
      setHostState(root, 'configuration_error', 'Journey host configuration is incomplete.');
      return;
    }
    unmount = mountPwa(pwa) || (() => {});
    setHostState(root, 'authenticated', 'Journey workspace ready.');
  };

  const onIdentity = (event) => {
    const identity = event.detail;
    if (identity?.authenticationStatus === 'authenticated' && identity.provider === 'shopify'
      && Number.isSafeInteger(identity.expiresAt)) {
      mountAuthenticatedPwa();
      return;
    }
    bridge.onSessionExpired();
  };
  root.addEventListener(JOURNEY_IDENTITY_EVENT, onIdentity);

  const controller = {
    dispose() {
      if (disposed) return;
      disposed = true;
      root.removeEventListener(JOURNEY_IDENTITY_EVENT, onIdentity);
      if (unmount) unmount();
      unmount = null;
      if (windowLike.ARVENTO_JOURNEY_HOST === bridge) delete windowLike.ARVENTO_JOURNEY_HOST;
      controllers.delete(root);
      delete root.dataset.journeyHostState;
    },
  };
  controllers.set(root, controller);
  return controller;
}

function rootFromSection(section) {
  if (section?.matches?.('[data-arvento-journey-host]')) return section;
  return section?.querySelector?.('[data-arvento-journey-host]') || null;
}

async function initJourneyHostWithAuth(root) {
  try {
    const auth = await import(root.dataset.authModuleUrl);
    if (typeof auth.getJourneySession !== 'function' || typeof auth.clearJourneySession !== 'function') {
      throw new Error('AUTH_MODULE_INVALID');
    }
    initJourneyHost(root, {
      getJourneySession: auth.getJourneySession,
      clearJourneySession: auth.clearJourneySession,
    });
  } catch {
    setHostState(root, 'configuration_error', 'Journey host configuration is incomplete.');
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-arvento-journey-host]').forEach(initJourneyHostWithAuth);
  document.addEventListener('shopify:section:load', (event) => {
    const root = rootFromSection(event.target);
    if (root) initJourneyHostWithAuth(root);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = rootFromSection(event.target);
    controllers.get(root)?.dispose();
  });
}
